import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsIcon from '@mui/icons-material/Directions';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useConfig } from '../../../utils/useConfig';
import SocialMediaIcons from '../../../components/SocialMediaIcons';

// Definiujemy typy dla kolorów i kart kontaktowych
type ColorKey = 'blue' | 'green' | 'red' | 'amber';

type ContactCard = {
  id: string;
  icon: JSX.Element;
  title: string;
  content: JSX.Element;
  action?: {
    text: string;
    url: string;
  };
  color: ColorKey;
};

function Contact() {
  const config = useConfig();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const contactCards: ContactCard[] = [
    {
      id: 'address',
      icon: <LocationOnIcon fontSize="large" />,
      title: 'Adres',
      content: (
        <>
          {config.company.name}
          <br />
          {config.contact.address.street}
          <br />
          {config.contact.address.postal} {config.contact.address.city}
          <br />
          {config.contact.address.region && config.contact.address.region}
        </>
      ),
      action: {
        text: 'Zobacz na mapie',
        url: 'https://maps.app.goo.gl/PHmh3NSaup2QxEeW6',
      },
      color: 'blue',
    },
    {
      id: 'hours',
      icon: <AccessTimeIcon fontSize="large" />,
      title: 'Godziny pracy',
      content: (
        <>
          Poniedziałek - Piątek
          <br />
          7:30 - 15:00
          <br />
          Weekend: Zamknięte
        </>
      ),
      color: 'green',
    },
    {
      id: 'phone',
      icon: <PhoneIcon fontSize="large" />,
      title: 'Telefon',
      content: (
        <>
          <a
            href="tel:+48604856094"
            className="text-lg font-medium hover:underline"
          >
            +48 604 856 094
          </a>
          <br />
          <span className="text-sm text-gray-500">Dział handlowy</span>
        </>
      ),
      color: 'red',
    },
    {
      id: 'email',
      icon: <EmailIcon fontSize="large" />,
      title: 'E-mail',
      content: (
        <>
          <a
            href={`mailto:${config.contact.email}`}
            className="text-lg font-medium hover:underline"
          >
            {config.contact.email}
          </a>
          <br />
          <span className="text-sm text-gray-500">Zapytania ogólne</span>
        </>
      ),
      color: 'amber',
    },
  ];

  return (
    <div className="w-full py-12 md:py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto">
        {/* Nagłówek sekcji */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-black to-primary">
              Skontaktuj się z nami
            </span>
          </h2>
          <div className="mx-auto w-16 md:w-20 h-1.5 rounded-full mb-3 md:mb-4 bg-primary"></div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
            Jesteśmy dostępni, aby odpowiedzieć na Twoje pytania i pomóc w
            doborze odpowiednich produktów
          </p>
        </motion.div>

        {/* Karty kontaktowe */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="mb-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactCards.map((card) => (
              <motion.div
                key={card.id}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300"
                whileHover={{
                  y: -5,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={() => setHoveredCard(card.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10 text-primary mr-3">
                      {card.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {card.title}
                    </h3>
                  </div>

                  <div className="text-gray-700 mb-4">{card.content}</div>

                  {card.action && (
                    <motion.a
                      href={card.action.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                      whileHover={{ x: 5 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {card.action.text}
                      <DirectionsIcon fontSize="small" className="ml-1" />
                    </motion.a>
                  )}
                </div>

                {/* Dekoracyjne elementy w tle */}
                <motion.div
                  className="absolute -right-8 -bottom-8 w-24 h-24 rounded-full bg-primary/5 z-0"
                  animate={{
                    scale: hoveredCard === card.id ? 1.2 : 1,
                  }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mapa Google */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={itemVariants}
          className="mb-12 rounded-xl overflow-hidden shadow-md border border-gray-200 h-[400px] relative"
        >
          <iframe
            src={config.contact.googleMaps}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen={false}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Lokalizacja firmy"
            className="absolute inset-0 w-full h-full"
          ></iframe>

          <div className="absolute top-4 right-4 z-10">
            <motion.a
              href="https://maps.app.goo.gl/PHmh3NSaup2QxEeW6"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg text-sm font-medium text-gray-800 shadow-md hover:bg-white transition-colors"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <OpenInNewIcon fontSize="small" />
              <span>Otwórz mapę</span>
            </motion.a>
          </div>
        </motion.div>

        {/* Sekcja Social Media i kontakt */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Social Media */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={itemVariants}
            className="md:col-span-2 bg-white rounded-xl shadow-md p-6 border border-gray-100"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Media społecznościowe
            </h3>
            <p className="text-gray-600 mb-6">
              Śledź nas w mediach społecznościowych, aby być na bieżąco z
              nowościami i promocjami.
            </p>

            <div className="flex flex-wrap gap-3">
              <SocialMediaIcons
                showLabels={true}
                className="justify-center md:justify-start"
              />
            </div>
          </motion.div>

          {/* Formularz lub CTA */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={itemVariants}
            className="md:col-span-3 rounded-xl overflow-hidden shadow-md relative"
          >
            <div className="absolute inset-0 bg-primary opacity-90 z-0"></div>

            <div className="relative z-10 p-6 md:p-8 text-white">
              <h3 className="text-2xl font-bold mb-3">Masz pytania?</h3>
              <p className="mb-6 opacity-90">
                Nasz zespół specjalistów jest gotowy, aby odpowiedzieć na
                wszystkie Twoje pytania dotyczące produktów, zamówień lub
                specjalnych wymagań.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`tel:${config.contact.phone}`}
                  className="inline-flex items-center justify-center gap-2 bg-white text-primary font-medium py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <PhoneIcon fontSize="small" />
                  <span>Zadzwoń teraz</span>
                </a>

                <a
                  href={`mailto:${config.contact.email}`}
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-medium py-3 px-6 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <EmailIcon fontSize="small" />
                  <span>Wyślij email</span>
                </a>
              </div>
            </div>

            {/* Dekoracyjne elementy tła */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-10 rounded-full -mr-20 -mt-20"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>
          </motion.div>
        </div>

        {/* Godziny otwarcia */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-50 rounded-full text-gray-700 text-sm">
            <AccessTimeIcon fontSize="small" className="text-gray-400" />
            <span>
              <strong>Godziny otwarcia:</strong> Poniedziałek - Piątek: 7:30 -
              15:00, Weekend: Zamknięte
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Contact;
